/** Entry point — bootstraps the Phaser game after the active font is loaded. */
import Phaser from 'phaser';
import { MenuScene } from './scenes/MenuScene';
import { BootScene } from './scenes/BootScene';
import { GameScene } from './scenes/GameScene';
import { UIScene } from './scenes/UIScene';
import { PauseScene } from './scenes/PauseScene';
import { KeyRemapScene } from './scenes/KeyRemapScene';
import { DebugSettingsScene } from './scenes/debug/DebugSettingsScene';
import { DebugSpawnScene } from './scenes/debug/DebugSpawnScene';
import { DebugDespawnScene } from './scenes/debug/DebugDespawnScene';
import { DebugGrantXPScene } from './scenes/debug/DebugGrantXPScene';
import { DebugSetHPScene } from './scenes/debug/DebugSetHPScene';
import { AttunementSceneText } from './scenes/AttunementScene';
import { AttunementTreeScene } from './scenes/AttunementTreeScene';
import { ForkChoiceScene } from './scenes/ForkChoiceScene';
import { GAME_WIDTH, GAME_HEIGHT, DPR, CANVAS_SCALE } from './utils/Constants';
import { Font } from './ui/Theme';
import { installDebugConsole } from './debug/DebugConsole';

// Force the browser to download the active font before starting the game.
// @font-face alone only *declares* a font — browsers don't fetch it until
// a visible DOM element uses it. Since Phaser draws to canvas (not DOM text),
// the font never gets requested. document.fonts.load() forces the download.
// Without this, Phaser silently falls back to the next font in the stack.
document.fonts.load(`16px ${Font.family.split(',')[0].trim()}`).then(() => {
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    width: Math.round(GAME_WIDTH * CANVAS_SCALE * DPR),
    height: Math.round(GAME_HEIGHT * CANVAS_SCALE * DPR),
    parent: document.body,
    backgroundColor: '#111111',
    fps: {
      smoothStep: false,
    },
    scene: [MenuScene, BootScene, GameScene, UIScene, PauseScene, KeyRemapScene, AttunementTreeScene, AttunementSceneText, ForkChoiceScene, DebugSettingsScene, DebugSpawnScene, DebugDespawnScene, DebugGrantXPScene, DebugSetHPScene],
    antialias: false,
    pixelArt: true,
    roundPixels: true,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false,
        fixedStep: false,
      },
    },
  };

  // In simulation mode (?sim=1), skip the menu and jump straight to asset loading.
  // BootScene loads assets then transitions to GameScene, which registers the
  // SimulationBridge on window.__gameTest for Playwright to drive.
  const simMode = new URLSearchParams(window.location.search).has('sim');
  if (simMode) {
    config.scene = [BootScene, GameScene, UIScene, PauseScene, ForkChoiceScene];
    // Headless Chromium has no window focus — without this, Phaser pauses
    // scene updates and GameScene.update() never runs after boot.
    // pauseOnBlur is accepted at runtime but missing from Phaser's TS types.
    (config as Record<string, unknown>).pauseOnBlur = false;
  }

  const game = new Phaser.Game(config);

  // When sim mode starts BootScene first, it needs save data that MenuScene normally provides.
  if (simMode) {
    game.scene.start('BootScene', { saveName: '__sim__', isNew: true });
  }

  installDebugConsole(game);
});
